import type { FailCode } from '@packages/models/api';

import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import {
    FetchWiringDiagramImage,
    FetchWiringDiagramSubsections,
    type WiringDiagramSubsectionEntity,
} from '@packages/domain/fail-codes';
import { container } from '@web/services/container';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { FailCodeSelection, FailCodeVehicleParams } from '../fail-codes-modal.types';

const logger = getLogger('techline');

/**
 * Parameters required by the electrical grid lookup hook. Uses {@link FailCodeVehicleParams}
 * which includes `vin`, required by the wiring diagram subsections endpoint.
 */
export type UseLookupByElectricalGridParams = FailCodeVehicleParams;

/**
 * A selectable wiring diagram option representing a subsection figure. Shaped for the `Typeahead`
 * component's option list. This is a presentation-layer type because `label` is a display concern.
 */
export type WiringDiagramOption = {
    /** The figure code identifier (e.g. "EWD-001"). */
    code: string;

    /** Display label combining figure code and subsection name. */
    label: string;

    /** The subsection/figure name (e.g. "Engine Control System"). */
    name: string;

    /** The x-axis label range for the electrical grid (e.g. "A-M" or "N-Z"). */
    range: string;
};

/** The two steps in the electrical grid lookup flow. */
type ElectricalGridStep = 'selectDiagram' | 'selectGridLocation';

// Operations are prefixed with $ to make them visually distinct in this file.
const [$fetchWiringDiagramImage, $fetchWiringDiagramSubsections] = container.resolveOperations(
    FetchWiringDiagramImage,
    FetchWiringDiagramSubsections
);

/**
 * Manages the two-step electrical grid lookup flow for selecting a fail code by clicking on
 * a wiring diagram.
 *
 * Step 1: Fetch available wiring diagram subsections and let the user select one.
 * Step 2: Fetch the diagram image, display it, and let the user click a grid cell to select
 * a fail code location.
 *
 * This hook owns all state for the flow: diagram options, selected diagram, diagram image URL,
 * selected fail code from the grid, current step, and zoom level.
 *
 * @param params - vehicle identification parameters including VIN
 * @returns hook state and handlers for the electrical grid lookup flow
 */
export function useLookupByElectricalGrid(params: UseLookupByElectricalGridParams) {
    // State
    const [currentStep, setCurrentStep] = useState<ElectricalGridStep>('selectDiagram');
    const [diagramImageUrl, setDiagramImageUrl] = useState('');
    const [diagramOptionData, setDiagramOptionData] = useState<WiringDiagramOption[]>([]);
    const [gridRange, setGridRange] = useState('');
    const [isLoadingDiagramImage, setIsLoadingDiagramImage] = useState(false);
    const [selectedDiagramData, setSelectedDiagramData] = useState<WiringDiagramOption[]>([]);
    const [selectedFailCodeData, setSelectedFailCodeData] = useState<FailCode | null>(null);
    const [zoom, setZoom] = useState(0);

    // Hooks
    const { abortSignalRef } = useAbortController();

    // Derived state
    const isOnSelectDiagramStep = currentStep === 'selectDiagram';
    const isOnSelectGridLocationStep = currentStep === 'selectGridLocation';
    const hasDiagramSelected = selectedDiagramData.length > 0;
    const hasFailCodeSelected = selectedFailCodeData !== null;
    const selectedDiagramName = hasDiagramSelected ? selectedDiagramData[0].name : '';

    // Refs
    const diagramImageUrlRef = useRef('');

    // Handlers

    /**
     * Fetch the wiring diagram image for a given figure code as a blob, then create an
     * object URL for display.
     *
     * @param figureCode - the figure code identifying the diagram to fetch
     * @param range - the x-axis range to set for the grid overlay
     */
    const fetchDiagramImage = useCallback(
        async (figureCode: string, range: string) => {
            setIsLoadingDiagramImage(true);

            try {
                // Fetch the image blob via the use case
                const blob = await $fetchWiringDiagramImage({ figureCode, signal: abortSignalRef.current });

                if (!blob) {
                    throw new Error('Failed to fetch wiring diagram image');
                }

                // Revoke any previously created blob URL before creating a new one
                if (diagramImageUrlRef.current) {
                    URL.revokeObjectURL(diagramImageUrlRef.current);
                }

                // Create and store the new blob URL
                const blobUrl = URL.createObjectURL(blob);
                diagramImageUrlRef.current = blobUrl;

                setDiagramImageUrl(blobUrl);
                setGridRange(range);
            } catch (error) {
                logger.error(`Failed to fetch wiring diagram image [${figureCode}]`, { figureCode, error });
            } finally {
                setIsLoadingDiagramImage(false);
            }
        },
        [abortSignalRef]
    );

    /**
     * Handle the user selecting a wiring diagram from the typeahead dropdown. Updates
     * the selected diagram state, which enables the "Continue" button.
     *
     * @param selected - the diagram option(s) chosen by the user
     */
    const handleDiagramTypeaheadChange = useCallback((selected: WiringDiagramOption[]) => {
        setSelectedDiagramData(selected);
    }, []);

    /**
     * Handle the user clicking "Continue" after selecting a diagram. Transitions to step 2
     * and fetches the diagram image as a blob URL.
     */
    const handleContinueButtonClick = useCallback(async () => {
        if (selectedDiagramData.length === 0) {
            return;
        }

        setCurrentStep('selectGridLocation');

        // Fetch the diagram image
        const diagram = selectedDiagramData[0];
        await fetchDiagramImage(diagram.code, diagram.range);
    }, [fetchDiagramImage, selectedDiagramData]);

    /**
     * Handle the user clicking "Back" from step 2 to return to step 1. Resets the grid state
     * but preserves the diagram selection so the user can re-select or change their choice.
     */
    const handleBackToSelectDiagramClick = useCallback(() => {
        setCurrentStep('selectDiagram');
        setSelectedFailCodeData(null);
        setDiagramImageUrl('');
        setGridRange('');
        setZoom(0);

        // Revoke the old blob URL to free memory
        if (diagramImageUrlRef.current) {
            URL.revokeObjectURL(diagramImageUrlRef.current);
            diagramImageUrlRef.current = '';
        }
    }, []);

    /**
     * Handle the user selecting a fail code by clicking on a grid cell in the electrical
     * diagram. Called by the `ElectricalGridSelector` child component.
     *
     * @param failCodeData - the fail code returned by the grid cell validation
     */
    const handleGridFailCodeSelect = useCallback((failCodeData: FailCode) => {
        setSelectedFailCodeData(failCodeData);
    }, []);

    /**
     * Handle the user clicking the zoom-in button. Increases the diagram zoom level by 30px.
     */
    const handleZoomInButtonClick = useCallback(() => {
        setZoom((prev) => prev + 30);
    }, []);

    /**
     * Handle the user clicking the zoom-out button. Decreases the diagram zoom level by 30px.
     */
    const handleZoomOutButtonClick = useCallback(() => {
        setZoom((prev) => prev - 30);
    }, []);

    /**
     * Build the final `FailCodeSelection` and invoke the confirm callback. Validates that a
     * fail code has been selected before proceeding.
     *
     * @returns the fail code selection if valid, or `null` if no fail code is selected
     */
    function buildFailCodeSelection(): FailCodeSelection | null {
        if (!selectedFailCodeData) {
            return null;
        }

        return {
            failCode: selectedFailCodeData.failCode,
            repairCode: selectedFailCodeData.failCode,
            section: 'Electrical Location Grids',
            subSection: selectedDiagramName,
        };
    }

    // Effects

    /**
     * Fetch the available wiring diagram subsections on mount. Populates the typeahead
     * options for step 1.
     */
    useEffect(() => {
        /** Fetch wiring diagram subsections and map them to typeahead options. */
        async function fetchDiagramOptions() {
            const subsections = await $fetchWiringDiagramSubsections({
                modelCode: params.modelCode,
                modelYear: params.modelYear,
                optionCode: params.optionCode,
                signal: abortSignalRef.current,
                vin: params.vin,
            });

            // Map domain entities to typeahead options
            const options: WiringDiagramOption[] = subsections.map((figure: WiringDiagramSubsectionEntity) => ({
                code: figure.figureCode,
                label: `${figure.figureCode} - ${figure.subsectionName}`,
                name: figure.subsectionName,
                range: figure.xAxisRange,
            }));

            setDiagramOptionData(options);
        }

        fetchDiagramOptions();
    }, [abortSignalRef, params.modelCode, params.modelYear, params.optionCode, params.vin]);

    /**
     * Revoke the blob URL on unmount to prevent memory leaks.
     */
    useEffect(() => {
        return () => {
            if (diagramImageUrlRef.current) {
                URL.revokeObjectURL(diagramImageUrlRef.current);
            }
        };
    }, []);

    return {
        /**
         * Build the final fail code selection from the current state. Returns `null` if no
         * fail code has been selected from the grid.
         */
        buildFailCodeSelection,

        /** The current step in the two-step flow. */
        currentStep,

        /** The blob URL for the selected wiring diagram image, or empty string if not yet loaded. */
        diagramImageUrl,

        /** Available wiring diagram options for the typeahead in step 1. */
        diagramOptionData,

        /** The x-axis range label for the grid overlay (e.g. "A-M" or "N-Z"). */
        gridRange,

        /** Whether a wiring diagram option has been selected in step 1. */
        hasDiagramSelected,

        /** Whether a fail code has been selected by clicking the grid in step 2. */
        hasFailCodeSelected,

        /** Handle the user clicking "Back" to return from step 2 to step 1. */
        handleBackToSelectDiagramClick,

        /** Handle the user clicking "Continue" after selecting a diagram in step 1. */
        handleContinueButtonClick,

        /** Handle the typeahead selection change for the diagram dropdown. */
        handleDiagramTypeaheadChange,

        /** Handle a fail code selection from the electrical grid component. */
        handleGridFailCodeSelect,

        /** Handle the zoom-in button click. */
        handleZoomInButtonClick,

        /** Handle the zoom-out button click. */
        handleZoomOutButtonClick,

        /** Whether the diagram image is currently being fetched. */
        isLoadingDiagramImage,

        /** Whether the current step is the diagram selection step. */
        isOnSelectDiagramStep,

        /** Whether the current step is the grid location selection step. */
        isOnSelectGridLocationStep,

        /** The display name of the currently selected diagram. */
        selectedDiagramName,

        /** The currently selected diagram option(s) from the typeahead. */
        selectedDiagramData,

        /** The zoom level offset in pixels for the diagram display. */
        zoom,
    };
}
